import { findIndex, isEmpty, isNil } from 'lodash';
import dayjs from 'src/utils/dayjs';
import { createSelector, type Selector } from 'reselect';
import type { AppState } from 'src/reducers';
import type { CardViewDataInputs, CardViewOptionalInputs } from 'src/components/StandardCardView/UIData.types';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { generateGroupBy } from 'src/pages/Hindsighting/StyleColorReview/TopTYvsLY/TopTYvsLY.container';
import { getDateFromWeek } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.utils';
import type { BasicItem, TreePivot, WorklistInfo } from 'src/worker/pivotWorker.types';
import type { FloorsetCmpSlice } from './FloorsetComparison.slice';
import { getDataFromCache } from 'src/services/pivotServiceCache';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { DaysRangeListResponse } from 'src/types/Scope';
import { getSelectedFloorset } from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';

const getStyles: Selector<AppState, TreePivot> = (state) => {
  return getDataFromCache(state.pages.assortmentBuild.floorsetComparison)?.tree || [];
};

export const getView: Selector<AppState, FloorsetCmpSlice> = (state) => {
  return state.pages.assortmentBuild.floorsetComparison;
};

const getSubheader: Selector<AppState, SubheaderSlice> = (state) => {
  return state.subheader;
};
const getWorklist: Selector<AppState, WorklistInfo[]> = (state) => {
  return state.pages.hindsighting.styleColorReview.sharedData.worklist;
};

export const getProcessedData = createSelector(
  getView,
  getStyles,
  getSubheader,
  getWorklist,
  (viewSlice, styles, subheader, worklist) => {
    if (viewSlice && viewSlice.groupingInfo && subheader) {
      const groupingInfo = viewSlice.groupingInfo;
      const groupBy = generateGroupBy(groupingInfo.dataIndex);

      const dataInputs: CardViewDataInputs = {
        styles: styles || null,
        subheader: { ...subheader, groupBy },
        defns: {
          view: viewSlice.viewDefn || {},
          rollup: viewSlice.calcViewDefn || {},
        },
      };
      const optionalInputs: CardViewOptionalInputs = {
        worklist,
        staticColumns: groupingInfo.staticColumns,
      };

      return processDataForCardView(dataInputs, optionalInputs);
    }
    return [];
  },
);

export const getCurrentFloorsetIndex: Selector<AppState, number> = (state) => {
  const { topMemberOptions } = state.subheader;
  const currentIndex =
    findIndex(topMemberOptions, (option) => {
      return option.id === getSelectedFloorset(state);
    }) ?? 0;
  return currentIndex;
};

const getLastUpdatedFloorsetIndex: Selector<AppState, number> = (state) => {
  return state.pages.assortmentBuild.floorsetComparison.floorsetIndexLastUpdated;
};

const getFloorsets: Selector<AppState, BasicItem[] | undefined> = (state) => {
  return !isEmpty(state.subheader.topMemberOptions) ? state.subheader.topMemberOptions : undefined;
};

const getScopeFloorsetSelection: Selector<AppState, string | null> = (state) => {
  return state.scope.selections.floorSet;
};

const getScopeLastUpdated: Selector<AppState, number | undefined> = (state) => {
  return state.scope.updatedAt;
};

const getDaysRangeList: Selector<AppState, DaysRangeListResponse> = (state) => {
  return state.scope.daysRangeList;
};

const getScopeStart: Selector<AppState, string | null> = (state) => {
  return state.scope.selections.start;
};

export const getSelectedFloorsetIndex = createSelector(
  getCurrentFloorsetIndex,
  getLastUpdatedFloorsetIndex,
  getFloorsets,
  getScopeFloorsetSelection,
  getScopeLastUpdated,
  getDaysRangeList,
  getScopeStart,
  (
    selectedFloorsetIndex,
    floorsetIndexLastUpdated,
    floorsets,
    scopeFloorsetId,
    scopeLastUpdated,
    daysRangeList,
    start,
  ) => {
    if (isEmpty(floorsets) || isNil(floorsets)) {
      return -1;
    }

    const viewFloorsetId = selectedFloorsetIndex >= 0 ? floorsets[selectedFloorsetIndex].id : '';

    if (!isNil(scopeLastUpdated) && !isNil(scopeFloorsetId)) {
      // checks for scope floorset selection
      // set selection to whichever was last selected (scope floorset dropdown or view's floorset dropdown)
      const mostRecentFloorsetId = scopeLastUpdated > floorsetIndexLastUpdated ? scopeFloorsetId : viewFloorsetId;
      return floorsets.findIndex((floorset) => floorset.id === mostRecentFloorsetId);
    }

    // The view already has a valid held selection (e.g. from switching between Floorset views) and
    // there's no scope floorset to compare it against — keep it rather than falling through to a
    // date-based guess.
    if (selectedFloorsetIndex >= 0) {
      return selectedFloorsetIndex;
    }

    if (!isNil(start)) {
      const startDate = getDateFromWeek(start, daysRangeList.start_date);

      if (isEmpty(startDate)) {
        return 0;
      }

      const startMonth = dayjs(startDate).format('YYYY-DD-MMM');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [year, day, month] = startMonth.split('-');
      const index = floorsets.findIndex(
        (floorset) => !isNil(floorset.id?.match(year)) && !isNil(floorset.id?.match(month)),
      );
      return index < 0 ? 0 : index;
    }

    return 0; // default to first item in array
  },
);
